// Фильтр справочника по нескольким группам мышц: выпадающий список с галочками, чипы выбранных
// и фигура, на которой группа выбирается нажатием на мышцу.

import { useEffect, useId, useMemo, useRef, useState } from 'react'

import { useBodySex } from '@/api/settings'
import { MuscleMap } from '@/components/muscle-map/MuscleMap'
import { toggleMuscle, type MuscleTone } from '@/components/muscle-map/muscles'
import { Icon } from '@/components/ui'
import { controlClass } from '@/components/ui/styles'
import { cn } from '@/lib/cn'
import { useMuscleLabels, useMuscles } from '@/pages/workouts/api'

/** Кнопка «Группы мышц» с выпадающим списком галочек. */
export function MuscleMultiSelect({ value, onChange }: { value: string[]; onChange: (value: string[]) => void }) {
  const muscles = useMuscles()
  const label = useMuscleLabels()
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const id = useId()

  useEffect(() => {
    if (!open) return
    const close = (event: Event) => {
      if (event instanceof KeyboardEvent ? event.key === 'Escape' : !root.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('pointerdown', close)
    document.addEventListener('keydown', close)
    return () => {
      document.removeEventListener('pointerdown', close)
      document.removeEventListener('keydown', close)
    }
  }, [open])

  const summary =
    value.length === 0 ? 'Все группы' : value.length <= 2 ? value.map(label).join(', ') : `Выбрано: ${value.length}`

  return (
    <div ref={root} className="relative flex min-w-0 flex-col gap-1.5">
      <span id={`${id}-label`} className="text-sm font-medium text-fg">
        Группы мышц
      </span>
      <button
        type="button"
        aria-labelledby={`${id}-label`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={`${id}-list`}
        onClick={() => setOpen((v) => !v)}
        className={cn(controlClass, 'flex items-center justify-between gap-2 pr-3 text-left')}
      >
        <span className="truncate">{summary}</span>
        <Icon name="chevron-down" size={18} className="shrink-0 text-muted" />
      </button>
      {open ? (
        <div
          id={`${id}-list`}
          className="absolute left-0 right-0 top-full z-20 mt-1 max-h-80 min-w-56 overflow-y-auto rounded-lg border border-border bg-surface p-1 shadow-lg"
        >
          {(muscles.data ?? []).map((m) => (
            <label key={m.key} className="flex min-h-10 cursor-pointer items-center gap-2.5 rounded-md px-2 text-sm hover:bg-surface-2">
              <input
                type="checkbox"
                className="h-4.5 w-4.5"
                checked={value.includes(m.key)}
                onChange={() => onChange(toggleMuscle(value, m.key))}
              />
              {m.label_ru}
            </label>
          ))}
          {value.length > 0 ? (
            <button
              type="button"
              className="mt-1 min-h-10 w-full rounded-md px-2 text-left text-sm text-accent hover:bg-surface-2"
              onClick={() => onChange([])}
            >
              Сбросить
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/** Выбранные группы чипами: крестик убирает группу. */
export function MuscleChips({ value, onChange }: { value: string[]; onChange: (value: string[]) => void }) {
  const label = useMuscleLabels()
  if (value.length === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {value.map((key) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(value.filter((k) => k !== key))}
          aria-label={`Убрать из фильтра: ${label(key)}`}
          className="inline-flex min-h-8 items-center gap-1 rounded-full border border-accent bg-accent-soft pl-3 pr-2 text-sm text-accent"
        >
          {label(key)}
          <Icon name="close" size={14} />
        </button>
      ))}
      <button type="button" onClick={() => onChange([])} className="hit-area ml-1 text-sm text-muted hover:text-accent">
        Сбросить
      </button>
    </div>
  )
}

/** Фигура-фильтр: выбранные группы подсвечены, нажатие на мышцу добавляет или убирает группу. */
export function MuscleMapFilter({ value, onChange }: { value: string[]; onChange: (value: string[]) => void }) {
  const sex = useBodySex()
  const label = useMuscleLabels()
  const tones = useMemo(
    () => Object.fromEntries(value.map((k) => [k, 'primary' as MuscleTone])),
    [value],
  )
  return (
    <div className="flex flex-col gap-2">
      <MuscleMap
        sex={sex}
        tones={tones}
        onSelect={(key) => onChange(toggleMuscle(value, key))}
        label={value.length ? `Выбраны: ${value.map(label).join(', ')}` : 'Группы мышц не выбраны'}
        className="mx-auto w-full max-w-xs"
      />
      <p className="text-xs text-muted">Нажми на мышцу — она добавится в фильтр; ещё раз — уберётся.</p>
    </div>
  )
}
